import { Box, useMediaQuery } from "@mui/material";
import {
  type CellContext,
  type ColumnDef,
  type SortingState,
} from "@tanstack/react-table";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Unauthorized } from "../../components";
import { DataTable } from "../../components/data-table/DataTable";
import { PageLayout } from "../../components/PageLayout";
import { useCheckAreaAccess } from "../../hooks/useCheckAreaAccess";
import { useLocalization } from "../../hooks/useLocalization";
import { Routes } from "../../types/NavigationPaths";
import { expirySortValue } from "../licenses/utils";
import { ExperienceTitleCell } from "./components/ExperienceTitleCell";
import { useExperiences } from "./hooks/useExperiences";
import { TExperience } from "./types";
import { formatDate } from "./utils";

const INITIAL_SORTING: SortingState = [{ id: "name", desc: false }];

const seatsLabel = (x: TExperience) => {
  const total = x.seats.total;

  return total == null ? `${x.seats.used} / ∞` : `${x.seats.used} / ${total}`;
};

const renderTitleCell = (ctx: CellContext<TExperience, unknown>) => (
  <ExperienceTitleCell
    isInternal={ctx.row.original.isInternal}
    value={ctx.getValue<string>()}
  />
);

export const ExperiencesPage: React.FC = () => {
  const { t } = useLocalization();
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { isLoading: isCheckAccessLoading, access } = useCheckAreaAccess(companyId);
  const areHooksEnabled = !!companyId && !isCheckAccessLoading && access.uam;

  const { loading, experiences } = useExperiences(areHooksEnabled);

  const isCompact = useMediaQuery("(max-width: 1200px)");

  const handleRowClick = React.useCallback(
    (experience: TExperience) => {
      void navigate(`/companies/${companyId}${Routes.EXPERIENCES}/${experience.id}`);
    },
    [navigate, companyId],
  );

  const columns = React.useMemo<ColumnDef<TExperience, any>[]>(() => {
    const w = isCompact ? 160 : 303;

    return [
      // 1. Title
      {
        id: "name",
        header: t("experiences.experiencesTableHeaders.title"),
        accessorKey: "name",
        cell: renderTitleCell,
        enableSorting: true,
        enableColumnFilter: false,
        sortingFn: "text",
        size: w,
        minSize: 120,
        maxSize: 500,
      },
      // 2. Company
      {
        id: "companyName",
        header: t("experiences.experiencesTableHeaders.company"),
        accessorKey: "companyName",
        cell: (ctx) => ctx.getValue<string>(),
        enableSorting: true,
        enableColumnFilter: true,
        sortingFn: "text",
        size: w,
        minSize: 120,
        maxSize: 500,
        filterFn: "arrIncludesSome",
      },
      // 3. Seats
      {
        id: "seats",
        header: t("experiences.experiencesTableHeaders.seats"),
        accessorFn: (row) => seatsLabel(row),
        sortingFn: (a, b) => a.original.seats.used - b.original.seats.used,
        enableSorting: true,
        enableColumnFilter: false,
        size: 96,
        minSize: 96,
        maxSize: 96,
      },
      // 4. Expiration Date
      {
        id: "expiresAt",
        header: t("experiences.experiencesTableHeaders.expirationDate"),
        accessorFn: (row) => formatDate(row.expiresAt),
        sortingFn: (a, b) =>
          expirySortValue(a.original.expiresAt) -
          expirySortValue(b.original.expiresAt),
        enableSorting: true,
        enableColumnFilter: false,
        size: 156,
        minSize: 156,
        maxSize: 156,
      },
    ];
  }, [isCompact, t]);

  if (!loading && !access.uam) {
    return (<Unauthorized />)
  }

  return (
    <PageLayout isLoading={loading} maxWidth={false}>
      <Box sx={{ width: "100%", overflowX: "auto" }}>
        <DataTable<TExperience>
          columns={columns}
          data={experiences}
          defaultSort={INITIAL_SORTING}
          facetedColumnIds={["companyName"]}
          getRowId={(r) => r.id}
          globalFilterPlaceholder={t("experiences.searchExperiences")}
          onRowClick={handleRowClick}
          overscan={8}
          showActionButton={false}
          virtualize
        />
      </Box>
    </PageLayout>
  );
};
